import {
    deepCopy,
    DynamicTableColumn,
    Organization,
    OrgPolicy,
    PolicyStatus,
    PolicyCategory,
    PolicyTargets,
    PolicyTargetMode,
    PolicyRevision,
    PolicyApprovalWorkflow,
    PolicyTargetsSnapshot,
    PolicyApprovalWorkflowSnapshot,
    parseEmbeddedTimestampsToFirebaseDate,
    TableAction,
} from '@newgenus/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import {
    Component,
    inject,
    Inject,
    OnInit,
    OnDestroy,
    ViewChild,
    ViewChildren,
    QueryList,
    ElementRef,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaterialModule } from '../../../material.module';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { PolicyAiDialogComponent, PolicyAiDialogResult } from './policy-ai-dialog/policy-ai-dialog.component';
import {
    OrgPolicyTargetSelectorComponent,
    PolicyTargetSelectorDialogInput,
    PolicyTargetSelectorDialogResult,
} from '../org-policy-target-selector/org-policy-target-selector';
import {
    OrgPolicyApproversSelectorComponent,
    PolicyApproversSelectorDialogInput,
    PolicyApproversSelectorDialogResult,
} from '../org-policy-approvers-selector/org-policy-approvers-selector';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Timestamp } from 'firebase/firestore';
import { take, Subscription, firstValueFrom } from 'rxjs';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { DialogService } from '../../../../services/dialog.service';
import { stampBlame } from '../../shared/policy-blame';
import { stampLastChangedBy } from '../../shared/last-changed-by';
import { PolicyApprovalService } from '../../services/policy-approval.service';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';

/**
 * Policy category options for dropdown
 */
export const POLICY_CATEGORIES: { value: PolicyCategory; label: string; icon: string }[] = [
    { value: 'general', label: 'General', icon: 'article' },
    { value: 'hr', label: 'Human Resources', icon: 'people' },
    { value: 'it', label: 'IT & Technology', icon: 'computer' },
    { value: 'finance', label: 'Finance', icon: 'attach_money' },
    { value: 'legal', label: 'Legal', icon: 'gavel' },
    { value: 'compliance', label: 'Compliance', icon: 'verified_user' },
    { value: 'safety', label: 'Health & Safety', icon: 'health_and_safety' },
    { value: 'operations', label: 'Operations', icon: 'settings' },
    { value: 'custom', label: 'Custom', icon: 'category' },
];

/**
 * Policy status display configuration
 */
export const POLICY_STATUS_CONFIG: Record<PolicyStatus, { label: string; icon: string; color: string }> = {
    draft: { label: 'Draft', icon: 'edit', color: 'default' },
    pending_approval: { label: 'Pending Approval', icon: 'hourglass_empty', color: 'warn' },
    approved: { label: 'Approved', icon: 'check_circle', color: 'primary' },
    scheduled: { label: 'Scheduled', icon: 'schedule', color: 'primary' },
    published: { label: 'Published', icon: 'publish', color: 'accent' },
    expired: { label: 'Expired', icon: 'event_busy', color: 'default' },
    cancelled: { label: 'Cancelled', icon: 'cancel', color: 'warn' },
    revoked: { label: 'Revoked', icon: 'block', color: 'warn' },
    archived: { label: 'Archived', icon: 'archive', color: 'default' },
    deleted: { label: 'Deleted', icon: 'delete', color: 'warn' },
};

@Component({
    selector: 'shared-org-policy-dialog',
    imports: [
        MaterialModule,
        ReactiveFormsModule,
        MatMenuModule,
        MatButtonModule,
        MatIconModule,
        CommonModule,
        NgxMatTimepickerModule,
        EditorModule,
    ],
    providers: [{ provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' }],
    templateUrl: './org-policy-dialog.html',
    styleUrls: ['./org-policy-dialog.scss', '../org-dialog.scss', '../../organization-board.component.scss'],
})
export class OrgPolicyDialogComponent implements OnInit, OnDestroy {
    // === ViewChild for focus management ===
    @ViewChild('policyNameInput') policyNameInput!: ElementRef<HTMLInputElement>;
    @ViewChildren(MatMenuTrigger) menuTriggers!: QueryList<MatMenuTrigger>;

    // === Configuration ===
    public policyCategories = POLICY_CATEGORIES;
    public statusConfig = POLICY_STATUS_CONFIG;

    // === Real-time subscription ===
    private policiesSubscription: Subscription | null = null;

    // === TinyMCE Editor State ===
    private editorReady = false;
    private editorInstance: any = null;
    private pendingEditorContent: string | null = null;

    // === Form Definition ===
    public form = new FormGroup({
        // Basic Info
        key: new FormControl<string | null>(null),
        name: new FormControl<string | null>(null, Validators.compose([Validators.required, Validators.minLength(3)])),
        description: new FormControl<string | null>(null),
        category: new FormControl<PolicyCategory>('general', Validators.required),
        customCategory: new FormControl<string | null>(null),

        // Content
        policyWriting: new FormControl<string | null>(
            null,
            Validators.compose([Validators.required, Validators.minLength(20)])
        ),

        // Version & Status
        version: new FormControl<number>(1),
        status: new FormControl<PolicyStatus>('draft'),

        // Dates
        effectiveDate: new FormControl<Date | null>(null),
        effectiveTime: new FormControl<string | null>(null),
        publishDate: new FormControl<Date | null>(null),
        publishTime: new FormControl<string | null>(null),
        expiryDate: new FormControl<Date | null>(null),
        expiryTime: new FormControl<string | null>(null),
        acknowledgmentDeadline: new FormControl<Date | null>(null),
        acknowledgmentDeadlineTime: new FormControl<string | null>(null),

        // Acknowledgment Settings
        requiresAcknowledgment: new FormControl<boolean>(false),
        isMandatory: new FormControl<boolean>(false),

        // Target Mode
        targetMode: new FormControl<PolicyTargetMode>('all_organization'),

        // Legacy fields for backwards compatibility
        createdDate: new FormControl<Date | null>(new Date()),
        lastModifiedBy: new FormControl<string | null>(null),
        lastModifiedDate: new FormControl<Date | null>(new Date()),
        revisions: new FormControl<PolicyRevision[] | null>(null),
    });

    // === Table Configuration ===
    public columns: DynamicTableColumn<OrgPolicy>[] = [
        {
            columnDef: 'status',
            header: 'Status',
            cell: (element: OrgPolicy) => element.status || 'draft',
        },
        {
            columnDef: 'name',
            header: 'Name',
            cell: (element: OrgPolicy) => element.name || '',
        },
        {
            columnDef: 'category',
            header: 'Category',
            cell: (element: OrgPolicy) => {
                const cat = this.policyCategories.find((c) => c.value === element.category);
                return cat?.label || element.category || 'General';
            },
        },
        {
            columnDef: 'modifiedOn',
            header: 'Last Updated',
            cell: (element: OrgPolicy) => {
                const date = element.modifiedOn;
                return date && date.toDate ? date.toDate().toLocaleDateString() : '';
            },
        },
        {
            columnDef: 'version',
            header: 'Version',
            cell: (element: OrgPolicy) => `v${element.version || 1}`,
        },
        {
            columnDef: 'actions',
            header: '',
            cell: () => '',
        },
    ];

    public columnActions: TableAction<OrgPolicy>[] = [
        {
            display: 'Edit',
            action: (row) => this.editPolicy(row),
            color: 'primary',
            disabled: false,
        },
        {
            display: 'View Details',
            action: (row) => this.viewPolicyDetails(row),
            color: 'accent',
            disabled: false,
        },
    ];

    public dataSource = new MatTableDataSource<OrgPolicy>([]);
    public displayedColumns = this.columns.map((c) => c.columnDef);
    public tabIndex = 0;
    public isEditMode = false;
    public editingPolicyKey: string | null = null;
    public editingPolicy: OrgPolicy | null = null; // Full policy object for editing
    public isEditingDeletedPolicy = false; // Track if we're editing a deleted policy
    public isEditingForNewVersion = false; // Track if user is editing to create a new version
    public originalPolicyBeforeNewVersion: OrgPolicy | null = null; // Store original policy to restore on cancel
    public processingPolicyKey: string | null = null; // Track which policy has an action in progress
    private processingStartTime = 0; // Track when processing started for minimum spinner time
    private processingExpectedStatus: PolicyStatus | null = null; // Status we expect after action completes
    private readonly MIN_SPINNER_TIME_MS = 1500; // Minimum time to show spinner

    // === TinyMCE Configuration ===
    public tinymceConfig: any = {
        base_url: '/tinymce',
        suffix: '.min',
        license_key: 'gpl',
        height: 350,
        menubar: false,
        skin: 'oxide-dark',
        content_css: 'dark',
        plugins: 'lists link table code help wordcount fullscreen',
        toolbar:
            'undo redo | blocks | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link table | code fullscreen',
        placeholder: 'Enter the full policy content here...',
        branding: false,
        promotion: false,
        readonly: false,
        // Fix: Close toolbar menus on scroll to prevent them from staying stuck
        toolbar_sticky: false,
        ui_mode: 'split',
    };

    // === Scroll handler reference for cleanup ===
    private scrollHandler: (() => void) | null = null;

    // === Status Filtering State ===
    public activeStatusFilter: PolicyStatus | null = null;
    private allPolicies: OrgPolicy[] = []; // Unfiltered policies
    private userCache: Map<string, string> = new Map(); // UID -> display name

    // === Target Selection State ===
    public selectedTargets: PolicyTargets | null = null;
    public targetSummary = { partCount: 0, hatCount: 0, userCount: 0 };

    // === Approval Workflow State ===
    public approvalWorkflow: PolicyApprovalWorkflow | null = null;
    public approvalStepCount = 0;

    // === Injected Services ===
    public snackbar = inject(MatSnackBar);
    private db = inject(AngularFirestore);
    private auth = inject(AngularFireAuth);
    private dialog = inject(MatDialog);
    private dialogService = inject(DialogService);
    private http = inject(HttpClient);
    /** The only route to an approval transition — see OE-75 / design §2e. */
    private approvalService = inject(PolicyApprovalService);

    /** Current user's Firebase ID token, for authenticating backend API calls. */
    private async getIdToken(): Promise<string> {
        const user = await this.auth.currentUser;
        return user ? user.getIdToken() : '';
    }

    /**
     * The acting user's uid — stamped as blame on every policy write via
     * `stampBlame`. Empty when nobody is signed in (no blame is then recorded).
     */
    private async currentUid(): Promise<string> {
        const user = await this.auth.currentUser;
        return user?.uid ?? '';
    }

    constructor(
        public dialogRef: MatDialogRef<OrgPolicyDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            organization: Organization;
            goToNewTab: boolean;
            apiBaseUrl?: string; // Optional API base URL for backend calls
            policyToEdit?: OrgPolicy; // Optional policy to load for editing
        }
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-160); phone-scoped in
        // global-styles.scss, desktop untouched.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    // === Processing State Helpers ===

    /**
     * Close any open menu triggers - called when async actions start
     */
    private closeAllMenus(): void {
        this.menuTriggers?.forEach((trigger) => {
            if (trigger.menuOpen) {
                trigger.closeMenu();
            }
        });
    }

    /**
     * Start showing spinner for a policy action
     * @param policyKey The policy being processed
     * @param expectedStatus The status we expect the policy to have when action completes
     */
    private startProcessing(policyKey: string, expectedStatus: PolicyStatus): void {
        console.log('🔄 Starting processing for policy:', policyKey, '→ expecting status:', expectedStatus);
        this.closeAllMenus(); // Close any open menus when action starts
        this.processingPolicyKey = policyKey;
        this.processingExpectedStatus = expectedStatus;
        this.processingStartTime = Date.now();
    }

    /**
     * Stop showing spinner, ensuring minimum display time
     * Called by subscription handler when status change is detected
     */
    private async stopProcessing(): Promise<void> {
        if (!this.processingPolicyKey) return;
        console.log('✅ Stopping processing, elapsed:', Date.now() - this.processingStartTime, 'ms');
        const elapsed = Date.now() - this.processingStartTime;
        const remaining = this.MIN_SPINNER_TIME_MS - elapsed;
        if (remaining > 0) {
            console.log('⏳ Waiting additional', remaining, 'ms for minimum spinner time');
            await new Promise((resolve) => setTimeout(resolve, remaining));
        }
        this.processingPolicyKey = null;
        this.processingExpectedStatus = null;
    }

    /**
     * Check if a policy status change indicates our processing action completed
     * Called from the subscription handler
     */
    private checkProcessingComplete(policies: OrgPolicy[]): void {
        if (!this.processingPolicyKey || !this.processingExpectedStatus) return;

        const policy = policies.find((p) => p.key === this.processingPolicyKey);
        if (policy && policy.status === this.processingExpectedStatus) {
            console.log('🎯 Status change detected for', this.processingPolicyKey, '→', policy.status);
            this.stopProcessing();
        }
    }

    // === Lifecycle ===
    public ngOnInit(): void {
        // Load policies from Firestore (not stale local data)
        this.refreshPoliciesList();

        // If a policy was passed for editing, load it immediately
        if (this.data.policyToEdit) {
            this.tabIndex = 0;
            // Pre-populate the form BEFORE TinyMCE initializes
            this.editPolicy(this.data.policyToEdit);
        } else if (this.data.goToNewTab) {
            this.tabIndex = 0;
            this.isEditMode = false;
        } else {
            this.tabIndex = 1;
        }
    }

    /**
     * Called when TinyMCE editor is fully initialized and ready
     */
    public onEditorInit(event: any): void {
        this.editorInstance = event.editor;
        this.editorReady = true;

        // Setup scroll handler to close toolbar menus when scrolling
        this.setupScrollHandler();

        // Force dark theme styles
        const doc = this.editorInstance.getDoc();
        if (doc && doc.body) {
            doc.body.style.backgroundColor = '#222f3e';
            doc.body.style.color = '#ffffff';
            doc.body.style.fontFamily = '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif';
            doc.body.style.fontSize = '14px';
            doc.body.style.lineHeight = '1.6';
            doc.body.style.padding = '8px';

            // Add style element for all text elements
            const style = doc.createElement('style');
            style.textContent = `
                body { background-color: #222f3e !important; color: #ffffff !important; }
                p, div, span, li, td, th, h1, h2, h3, h4, h5, h6 { color: #ffffff !important; }
                a { color: #4fc3f7 !important; }
                ::selection { background-color: #1976d2 !important; color: #ffffff !important; }
            `;
            doc.head.appendChild(style);
        }

        // If there's pending content, set it now
        if (this.pendingEditorContent) {
            this.editorInstance.setContent(this.pendingEditorContent);
            this.pendingEditorContent = null;
        }
    }

    // === Form Helpers ===
    public get columnsWithoutActions(): DynamicTableColumn[] {
        return this.columns.filter((c) => c.columnDef !== 'actions');
    }

    public get isCustomCategory(): boolean {
        return this.form.get('category')?.value === 'custom';
    }

    public get showAcknowledgmentOptions(): boolean {
        return this.form.get('requiresAcknowledgment')?.value === true;
    }

    /**
     * Whether the currently editing policy is published.
     * Used to restrict editing to only expiry date / acknowledgment deadline.
     */
    public get isPublishedPolicy(): boolean {
        return this.isEditMode && this.editingPolicy?.status === 'published';
    }

    /**
     * Whether form fields should be locked (published but NOT editing for new version).
     */
    public get shouldLockFields(): boolean {
        return this.isPublishedPolicy && !this.isEditingForNewVersion;
    }

    /**
     * Check if the expiry date or acknowledgment deadline has changed from the original published policy.
     */
    public hasExpiryDateChanged(): boolean {
        if (!this.editingPolicy) return false;

        const formExpiryDate = this.form.get('expiryDate')?.value;
        const formExpiryTime = this.form.get('expiryTime')?.value;
        const formDeadline = this.form.get('acknowledgmentDeadline')?.value;
        const formDeadlineTime = this.form.get('acknowledgmentDeadlineTime')?.value;

        const originalExpiry = this.editingPolicy.expiryDate?.toDate?.() || null;
        const originalDeadline = this.editingPolicy.acknowledgmentDeadline?.toDate?.() || null;

        // Check if expiry date changed
        const expiryChanged = this.hasDateFieldChanged(originalExpiry, formExpiryDate, formExpiryTime);
        // Check if acknowledgment deadline changed
        const deadlineChanged = this.hasDateFieldChanged(originalDeadline, formDeadline, formDeadlineTime);

        return expiryChanged || deadlineChanged;
    }

    /**
     * Compare an original date with form date+time values to detect changes.
     */
    private hasDateFieldChanged(
        original: Date | null,
        formDate: Date | null | undefined,
        formTime: string | null | undefined
    ): boolean {
        if (!original && !formDate) return false; // both empty
        if (!original && formDate) return true; // newly added
        if (original && !formDate) return true; // removed
        // Both have values — compare combined date+time
        const combined = this.combineDateAndTime(formDate!, formTime ?? null);
        return Math.abs(original!.getTime() - combined.getTime()) > 60000; // >1 minute difference
    }

    /**
     * Update only the expiry date and/or acknowledgment deadline on a published policy.
     * Does NOT change status, version, or any other field.
     */
    public async updatePublishedExpiry(): Promise<void> {
        if (!this.editingPolicyKey || !this.editingPolicy) return;
        if (this.editingPolicy.status !== 'published') return;

        const formValue = this.form.value;
        const now = Timestamp.now();
        const updateDoc: Record<string, unknown> = {
            modifiedOn: now,
        };

        try {
            // Get current user for audit
            const currentUser = await this.auth.currentUser;
            const currentUserName = currentUser?.displayName || currentUser?.email || 'Unknown User';
            stampBlame(updateDoc, currentUser?.uid);
            stampLastChangedBy(updateDoc, currentUser);

            // Build expiry date if set
            if (formValue.expiryDate) {
                // Ensure we have a Date object (datepicker may return a string or Date)
                const expiryDateObj =
                    formValue.expiryDate instanceof Date ? formValue.expiryDate : new Date(formValue.expiryDate);
                updateDoc['expiryDate'] = Timestamp.fromDate(
                    this.combineDateAndTime(expiryDateObj, formValue.expiryTime ?? null)
                );
            } else {
                // Explicitly clear expiry date if removed
                updateDoc['expiryDate'] = null;
            }

            // Build acknowledgment deadline if set
            if (formValue.acknowledgmentDeadline) {
                const deadlineDateObj =
                    formValue.acknowledgmentDeadline instanceof Date
                        ? formValue.acknowledgmentDeadline
                        : new Date(formValue.acknowledgmentDeadline);
                updateDoc['acknowledgmentDeadline'] = Timestamp.fromDate(
                    this.combineDateAndTime(deadlineDateObj, formValue.acknowledgmentDeadlineTime ?? null)
                );
            }

            // Add audit trail entry
            const expiryLabel = formValue.expiryDate
                ? `Expiry date updated to ${(formValue.expiryDate instanceof Date ? formValue.expiryDate : new Date(formValue.expiryDate)).toLocaleDateString()}`
                : 'Expiry date removed';
            const auditEntry = {
                action: 'expiry_updated',
                date: now,
                userId: currentUser?.uid || '',
                userName: currentUserName,
                details: expiryLabel,
            };
            const existingAudit = this.editingPolicy.auditTrail || [];
            updateDoc['auditTrail'] = [...existingAudit, auditEntry];

            // Save to Firestore (merge to only update these fields)
            const docPath = `organizations/${this.data.organization.key}/policies/${this.editingPolicyKey}`;
            console.log('📝 Writing to path:', docPath, 'updateDoc:', updateDoc);

            const docRef = this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policies')
                .doc(this.editingPolicyKey);

            docRef
                .update(updateDoc)
                .then(() => {
                    console.log('✅ Firestore update() resolved for:', docPath);
                    // Verify the write by reading back
                    docRef.ref.get().then((snap) => {
                        const data = snap.data();
                        console.log(
                            '🔍 Verification read - expiryDate:',
                            data?.['expiryDate'],
                            'modifiedOn:',
                            data?.['modifiedOn']
                        );
                    });
                    this.snackbar.open('Expiry date updated successfully', 'Close', { duration: 3000 });
                    this.resetForm();
                    this.tabIndex = 1;
                    this.refreshPoliciesList();
                })
                .catch((error) => {
                    console.error('❌ Firestore update() error:', error);
                    this.snackbar.open('Error updating expiry date: ' + error.message, 'Close', { duration: 5000 });
                });
        } catch (err) {
            console.error('Error in updatePublishedExpiry:', err);
            this.snackbar.open('Error updating expiry date', 'Close', { duration: 3000 });
        }
    }

    public getCategoryIcon(category: PolicyCategory | null | undefined): string {
        if (!category) return 'article';
        return this.policyCategories.find((c) => c.value === category)?.icon || 'article';
    }

    public getCategoryLabel(category: PolicyCategory | null | undefined): string {
        if (!category) return 'General';
        return this.policyCategories.find((c) => c.value === category)?.label || 'General';
    }

    public getStatusConfig(status: PolicyStatus) {
        return this.statusConfig[status] || this.statusConfig['draft'];
    }

    /**
     * Get approval workflow info for displaying in the policy list
     */
    public getApprovalInfo(policy: OrgPolicy): {
        hasWorkflow: boolean;
        totalSteps: number;
        approvedSteps: number;
        pendingSteps: number;
        rejectedSteps: number;
    } {
        if (!policy.approvalWorkflow?.steps || policy.approvalWorkflow.steps.length === 0) {
            return { hasWorkflow: false, totalSteps: 0, approvedSteps: 0, pendingSteps: 0, rejectedSteps: 0 };
        }

        const steps = policy.approvalWorkflow.steps;
        const totalSteps = steps.length;
        const approvedSteps = steps.filter((s) => s.status === 'approved').length;
        const rejectedSteps = steps.filter((s) => s.status === 'rejected').length;
        const pendingSteps = totalSteps - approvedSteps - rejectedSteps;

        return { hasWorkflow: true, totalSteps, approvedSteps, pendingSteps, rejectedSteps };
    }

    /**
     * Get the resolved hat count for a policy.
     * Checks resolvedHatCount, resolvedHatKeys, and legacy policyKeys on hats.
     */
    public getResolvedHatCount(policy: OrgPolicy): number {
        // First check if we have resolvedHatCount (preferred)
        if (policy.targets?.resolvedHatCount) {
            return policy.targets.resolvedHatCount;
        }

        // Check resolvedHatKeys array
        if (policy.targets?.resolvedHatKeys?.length) {
            return policy.targets.resolvedHatKeys.length;
        }

        // Legacy model: count hats that have this policy in their policyKeys
        const orgHats = this.data.organization?.orgHats;
        if (!orgHats?.hatsKeys || !policy.key) {
            return 0;
        }

        let count = 0;
        for (const hatKey of orgHats.hatsKeys) {
            const hat = orgHats[hatKey];
            if (hat && typeof hat === 'object' && 'policyKeys' in hat) {
                const hatPolicyKeys = (hat as any).policyKeys || {};
                if (policy.key in hatPolicyKeys || Object.values(hatPolicyKeys).includes(policy.key)) {
                    count++;
                }
            }
        }
        return count;
    }

    // === Date/Time Helpers ===

    /**
     * Combine a Date and time string (HH:MM AM/PM or HH:MM) into a single Date
     */
    private combineDateAndTime(date: Date, time: string | null): Date {
        if (!time) {
            return date; // Return date with default time (midnight)
        }

        const result = new Date(date);
        const parsed = this.parseTimeString(time);
        result.setHours(parsed.hours, parsed.minutes, 0, 0);
        return result;
    }

    /**
     * Parse a time string (12h or 24h format) into hours and minutes
     */
    private parseTimeString(time: string): { hours: number; minutes: number } {
        // Handle 12-hour format (e.g., "2:30 PM", "11:00 AM")
        const match12h = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (match12h) {
            let hours = parseInt(match12h[1], 10);
            const minutes = parseInt(match12h[2], 10);
            const period = match12h[3].toUpperCase();

            if (period === 'PM' && hours !== 12) {
                hours += 12;
            } else if (period === 'AM' && hours === 12) {
                hours = 0;
            }

            return { hours, minutes };
        }

        // Handle 24-hour format (e.g., "14:30", "09:00")
        const match24h = time.match(/^(\d{1,2}):(\d{2})$/);
        if (match24h) {
            return {
                hours: parseInt(match24h[1], 10),
                minutes: parseInt(match24h[2], 10),
            };
        }

        // Default to midnight if parsing fails
        return { hours: 0, minutes: 0 };
    }

    /**
     * Extract time string from a Date object in 12-hour format
     * Always returns the time, including midnight (12:00 AM)
     */
    private extractTimeFromDate(date: Date | null): string | null {
        if (!date) return null;

        let hours = date.getHours();
        const minutes = date.getMinutes();
        const period = hours >= 12 ? 'PM' : 'AM';

        // Convert to 12-hour format
        if (hours === 0) {
            hours = 12;
        } else if (hours > 12) {
            hours -= 12;
        }

        return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
    }

    // === Status Filtering ===

    /**
     * Filter policies by status
     */
    public filterByStatus(status: PolicyStatus | null): void {
        this.activeStatusFilter = status;
        if (status === null) {
            this.dataSource.data = this.allPolicies;
        } else {
            this.dataSource.data = this.allPolicies.filter((p) => (p.status || 'draft') === status);
        }
    }

    /**
     * Get count of policies with a specific status
     */
    public getStatusCount(status: PolicyStatus | null): number {
        if (status === null) {
            return this.allPolicies.length;
        }
        return this.allPolicies.filter((p) => (p.status || 'draft') === status).length;
    }

    /**
     * Get creator display name from UID
     */
    public getCreatorName(uid: string | undefined): string {
        if (!uid) return 'Unknown';

        // Check cache first
        if (this.userCache.has(uid)) {
            return this.userCache.get(uid)!;
        }

        // Set loading state IMMEDIATELY to prevent infinite loop
        this.userCache.set(uid, 'Loading...');

        // Load user from organization's users subcollection
        this.loadUserName(uid);
        return 'Loading...';
    }

    /**
     * Load user name from Firestore and cache it
     */
    private loadUserName(uid: string): void {
        // First try organization users subcollection
        this.db
            .collection('organizations')
            .doc(this.data.organization.key)
            .collection('users')
            .doc(uid)
            .get()
            .pipe(take(1))
            .subscribe((doc) => {
                if (doc.exists) {
                    const userData = doc.data() as { displayName?: string; name?: string; email?: string };
                    const name =
                        userData?.displayName || userData?.name || userData?.email || uid.substring(0, 8) + '...';
                    this.userCache.set(uid, name);
                } else {
                    // Fallback to main users collection
                    this.db
                        .collection('users')
                        .doc(uid)
                        .get()
                        .pipe(take(1))
                        .subscribe((userDoc) => {
                            if (userDoc.exists) {
                                const userData = userDoc.data() as {
                                    displayName?: string;
                                    name?: string;
                                    email?: string;
                                };
                                const name =
                                    userData?.displayName ||
                                    userData?.name ||
                                    userData?.email ||
                                    uid.substring(0, 8) + '...';
                                this.userCache.set(uid, name);
                            } else {
                                this.userCache.set(uid, uid.substring(0, 8) + '...');
                            }
                        });
                }
            });
    }

    // === Actions ===

    /**
     * Helper to deeply remove undefined values from an object (Firestore doesn't accept undefined)
     * Recursively processes nested objects and arrays
     */
    private removeUndefinedFields<T>(obj: T): T {
        if (obj === null || obj === undefined) {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => this.removeUndefinedFields(item)) as T;
        }

        if (typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof Timestamp)) {
            const result: Record<string, unknown> = {};
            for (const key in obj) {
                const value = (obj as Record<string, unknown>)[key];
                if (value !== undefined) {
                    result[key] = this.removeUndefinedFields(value);
                }
            }
            return result as T;
        }

        return obj;
    }

    /**
     * Generate change notes by comparing original policy with current form values
     */
    private generateChangeNotes(formValue: typeof this.form.value, targetsData: PolicyTargets): string {
        const changes: string[] = [];
        const original = this.editingPolicy;

        if (!original) {
            return 'Initial version';
        }

        // Compare basic fields
        if (formValue.name !== original.name) {
            changes.push(`Name: "${original.name}" → "${formValue.name}"`);
        }

        if (formValue.description !== (original.description || null)) {
            changes.push('Description updated');
        }

        if (formValue.category !== original.category) {
            const oldCat = this.policyCategories.find((c) => c.value === original.category)?.label || original.category;
            const newCat =
                this.policyCategories.find((c) => c.value === formValue.category)?.label || formValue.category;
            changes.push(`Category: ${oldCat} → ${newCat}`);
        }

        if (formValue.policyWriting !== original.policyWriting) {
            changes.push('Policy content updated');
        }

        // Compare acknowledgment settings
        if (formValue.requiresAcknowledgment !== (original.requiresAcknowledgment || false)) {
            changes.push(
                formValue.requiresAcknowledgment ? 'Acknowledgment now required' : 'Acknowledgment no longer required'
            );
        }

        // Compare targets
        const originalTargets = original.targets;
        const newMode = targetsData.mode;
        const oldMode = originalTargets?.mode || 'all_organization';

        if (newMode !== oldMode) {
            const modeLabels: Record<string, string> = {
                all_organization: 'All Organization',
                selected: 'Selected Targets',
            };
            changes.push(`Target audience: ${modeLabels[oldMode] || oldMode} → ${modeLabels[newMode] || newMode}`);
        }

        // Compare selected parts
        const oldPartCount = originalTargets?.parts?.length || 0;
        const newPartCount = targetsData.parts?.length || 0;
        if (oldPartCount !== newPartCount) {
            if (oldPartCount === 0 && newPartCount > 0) {
                changes.push(`Added ${newPartCount} target part${newPartCount > 1 ? 's' : ''}`);
            } else if (oldPartCount > 0 && newPartCount === 0) {
                changes.push('Removed all target parts');
            } else {
                changes.push(`Target parts: ${oldPartCount} → ${newPartCount}`);
            }
        }

        // Compare selected users
        const oldUserCount = originalTargets?.directUsers?.length || 0;
        const newUserCount = targetsData.directUsers?.length || 0;
        if (oldUserCount !== newUserCount) {
            if (oldUserCount === 0 && newUserCount > 0) {
                changes.push(`Added ${newUserCount} direct user${newUserCount > 1 ? 's' : ''}`);
            } else if (oldUserCount > 0 && newUserCount === 0) {
                changes.push('Removed all direct users');
            } else {
                changes.push(`Direct users: ${oldUserCount} → ${newUserCount}`);
            }
        }

        // Compare selected hats
        const oldHatCount = originalTargets?.directHats?.length || 0;
        const newHatCount = targetsData.directHats?.length || 0;
        if (oldHatCount !== newHatCount) {
            if (oldHatCount === 0 && newHatCount > 0) {
                changes.push(`Added ${newHatCount} target hat${newHatCount > 1 ? 's' : ''}`);
            } else if (oldHatCount > 0 && newHatCount === 0) {
                changes.push('Removed all target hats');
            } else {
                changes.push(`Target hats: ${oldHatCount} → ${newHatCount}`);
            }
        }

        // Compare approval workflow
        const oldStepCount = original.approvalWorkflow?.steps?.length || 0;
        const newStepCount = this.approvalWorkflow?.steps?.length || 0;
        if (oldStepCount !== newStepCount) {
            if (oldStepCount === 0 && newStepCount > 0) {
                changes.push(`Added approval workflow (${newStepCount} step${newStepCount > 1 ? 's' : ''})`);
            } else if (oldStepCount > 0 && newStepCount === 0) {
                changes.push('Removed approval workflow');
            } else {
                changes.push(`Approval workflow: ${oldStepCount} → ${newStepCount} steps`);
            }
        }

        // Compare dates
        if (this.hasDateChanged(original.effectiveDate, formValue.effectiveDate)) {
            changes.push('Effective date updated');
        }
        if (this.hasDateChanged(original.publishDate, formValue.publishDate)) {
            changes.push('Publish date updated');
        }
        if (this.hasDateChanged(original.expiryDate, formValue.expiryDate)) {
            changes.push('Expiry date updated');
        }

        return changes.length > 0 ? changes.join('; ') : 'Minor updates';
    }

    /**
     * Helper to compare dates (handles Timestamp vs Date comparison)
     */
    private hasDateChanged(original: Date | Timestamp | null | undefined, newDate: Date | null | undefined): boolean {
        if (!original && !newDate) return false;
        if (!original && newDate) return true;
        if (original && !newDate) return true;

        const origTime = original instanceof Timestamp ? original.toDate().getTime() : (original as Date).getTime();
        const newTime = newDate!.getTime();

        // Consider changed if difference is more than 1 minute (to account for time component changes)
        return Math.abs(origTime - newTime) > 60000;
    }

    public savePolicy(): void {
        // console.log('Save policy: ', this.form.value);

        if (this.form.invalid) {
            this.snackbar.open('Please fill in all required fields', 'Close', { duration: 3000 });
            return;
        }

        const formValue = this.form.value;

        // Check if this is a published policy being rescheduled to future date
        if (this.isEditMode && formValue.status === 'published' && formValue.publishDate) {
            const publishDateTime = this.combineDateAndTime(formValue.publishDate, formValue.publishTime ?? null);
            const now = new Date();

            if (publishDateTime > now) {
                // Show confirmation dialog before unpublishing
                this.showUnpublishWarningDialog(publishDateTime);
                return;
            }
        }

        // Continue with normal save
        this.executeSavePolicy();
    }

    /**
     * Show warning dialog when user tries to set future publish date on published policy
     */
    private async showUnpublishWarningDialog(newPublishDate: Date): Promise<void> {
        const formattedDate = newPublishDate.toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });

        const confirmed = await this.dialogService.asyncConfirm(
            '⚠️ Unpublish Policy?',
            `This policy is currently published and visible to target users.\n\n` +
                `Changing the publish date to a future date will:\n` +
                `• Unpublish the policy immediately\n` +
                `• Remove it from target users' view\n` +
                `• Schedule it to be republished on: ${formattedDate}\n\n` +
                `Acknowledgment records will be preserved.`
        );

        if (confirmed) {
            // Change status to scheduled and save
            this.form.patchValue({ status: 'scheduled' });
            this.executeSavePolicy();
        }
    }

    /**
     * Execute the actual save operation
     * @param isReinstatement - If true, also clears deletion-related fields
     * @returns the saved policy key, or null if the write failed. `submitForApproval`
     *          needs both — it must know the key of a just-created policy and must
     *          not call the approval endpoint on a policy that never landed.
     */
    private async executeSavePolicy(isReinstatement = false): Promise<string | null> {
        // Get current user for writtenBy field
        const currentUser = await this.auth.currentUser;
        const currentUserName = currentUser?.displayName || currentUser?.email || 'Unknown User';

        const formValue = this.form.value;

        // Show warning if no approval workflow configured
        if (!this.approvalWorkflow || this.approvalWorkflow.steps.length === 0) {
            this.snackbar.open('⚠️ No approval workflow configured - policy can be approved directly', 'OK', {
                duration: 5000,
                panelClass: ['warning-snackbar'],
            });
        }

        const policyKey = formValue.key || this.db.createId();
        const now = Timestamp.now();

        // Build targets object - include full selection if available
        let targetsData: PolicyTargets = this.selectedTargets || {
            mode: formValue.targetMode || 'all_organization',
        };
        // Ensure mode is set from form
        targetsData.mode = formValue.targetMode || targetsData.mode || 'all_organization';
        // Clean undefined values from targets (Firebase doesn't accept undefined)
        targetsData = this.removeUndefinedFields(targetsData);

        // Build the policy document - use null instead of undefined for optional fields
        const policyDoc: Record<string, unknown> = {
            key: policyKey,
            name: formValue.name!,
            description: formValue.description || null,
            category: formValue.category || 'general',
            policyWriting: formValue.policyWriting || '',
            version: formValue.version || 1,
            status: formValue.status || 'draft',

            // Acknowledgment settings
            requiresAcknowledgment: formValue.requiresAcknowledgment || false,
            isMandatory: formValue.isMandatory || false,

            // Targets - include full selection (parts, hats, users)
            targets: targetsData,

            // Revisions
            revisions: formValue.revisions || [],

            // Audit
            modifiedOn: now,
        };

        // Add approval workflow - save even if empty (to clear previous workflow)
        // Use null for empty workflow to properly clear it in Firestore
        if (this.approvalWorkflow && this.approvalWorkflow.steps.length > 0) {
            policyDoc['approvalWorkflow'] = this.approvalWorkflow;
        } else if (this.isEditMode) {
            // If editing and workflow is empty/cleared, explicitly set to null to remove it
            policyDoc['approvalWorkflow'] = null;
        }

        // Only add customCategory if category is 'custom' and value exists
        if (formValue.category === 'custom' && formValue.customCategory) {
            policyDoc['customCategory'] = formValue.customCategory;
        }

        // Only add dates if they have values (convert to Timestamps, combining date + time)
        if (formValue.effectiveDate) {
            policyDoc['effectiveDate'] = Timestamp.fromDate(
                this.combineDateAndTime(formValue.effectiveDate, formValue.effectiveTime ?? null)
            );
        }
        if (formValue.publishDate) {
            policyDoc['publishDate'] = Timestamp.fromDate(
                this.combineDateAndTime(formValue.publishDate, formValue.publishTime ?? null)
            );
        }
        if (formValue.expiryDate) {
            policyDoc['expiryDate'] = Timestamp.fromDate(
                this.combineDateAndTime(formValue.expiryDate, formValue.expiryTime ?? null)
            );
        }

        // Debug: Log all date/time form values
        console.log('📅 Date/Time Form Values:', {
            acknowledgmentDeadline: formValue.acknowledgmentDeadline,
            acknowledgmentDeadlineTime: formValue.acknowledgmentDeadlineTime,
            effectiveDate: formValue.effectiveDate,
            effectiveTime: formValue.effectiveTime,
            publishDate: formValue.publishDate,
            publishTime: formValue.publishTime,
            expiryDate: formValue.expiryDate,
            expiryTime: formValue.expiryTime,
        });

        if (formValue.acknowledgmentDeadline) {
            const combined = this.combineDateAndTime(
                formValue.acknowledgmentDeadline,
                formValue.acknowledgmentDeadlineTime ?? null
            );
            console.log('📅 Combined acknowledgmentDeadline:', combined);
            policyDoc['acknowledgmentDeadline'] = Timestamp.fromDate(combined);
        }

        // Blame: record the acting user (NOT the org key — that was the OE-72 bug)
        const isCreate = !formValue.key;
        stampBlame(policyDoc, currentUser?.uid, { create: isCreate });
        stampLastChangedBy(policyDoc, currentUser);

        // If creating new, add createdOn
        if (isCreate) {
            policyDoc['createdOn'] = now;
            // Exit early - savePolicyToFirestore handles the create write
            return this.savePolicyToFirestore(policyKey, policyDoc, formValue);
        }

        // DEBUG: Log the document being saved
        console.log('📋 Saving policyDoc:', JSON.stringify(policyDoc, null, 2));
        console.log('📋 Form values:', this.form.value);

        // If editing, add to revisions with full snapshot of the NEW state
        // This represents "what the policy looks like at this revision"
        // Users can "restore to" this revision to get this state back
        if (this.isEditMode && this.editingPolicyKey && this.editingPolicy) {
            const original = this.editingPolicy;

            // Build targets snapshot from the NEW targets being saved (not original)
            // This way "Revision X" shows the state AT revision X (after this save)
            // Store FULL target data for complete restoration capability
            const targetsSnapshot: PolicyTargetsSnapshot = {
                mode: targetsData.mode || 'all_organization',
                // Full target data - allows complete restore
                parts: targetsData.parts || [],
                directUsers: targetsData.directUsers || [],
                directHats: targetsData.directHats || [],
                // Summary data - for quick display
                partNames: targetsData.parts?.map((p) => p.name) || [],
                directUserNames: targetsData.directUsers?.map((u) => u.name) || [],
                directHatNames: targetsData.directHats?.map((h) => h.name) || [],
                partCount: targetsData.parts?.length || 0,
                directUserCount: targetsData.directUsers?.length || 0,
                directHatCount: targetsData.directHats?.length || 0,
            };

            // Build approval workflow snapshot from the NEW workflow being saved
            const workflowSnapshot: PolicyApprovalWorkflowSnapshot | undefined = this.approvalWorkflow
                ? {
                      approverNames: this.approvalWorkflow.steps?.map((s) => s.approver?.name || 'Unknown') || [],
                      stepCount: this.approvalWorkflow.steps?.length || 0,
                      workflowStatus: this.approvalWorkflow.workflowStatus,
                  }
                : undefined;

            // Generate meaningful change notes by comparing with original
            const changeNotes = this.generateChangeNotes(formValue, targetsData);

            // Revision captures the NEW state (what the policy looks like AFTER this save)
            // "Revision 1" = the policy at version 1 (after first save)
            const revision: PolicyRevision = {
                // Basic fields - capture the NEW values
                version: original.version || 1,
                date: now,
                name: formValue.name || '',
                policyWriting: formValue.policyWriting || '',
                writtenBy: currentUserName,
                changeNotes: changeNotes,

                // Full snapshot fields - capture the NEW values
                description: formValue.description || undefined,
                category: formValue.category || undefined,
                customCategory: formValue.category === 'custom' ? formValue.customCategory || undefined : undefined,
                status: formValue.status || undefined,

                // Dates snapshot - capture the NEW values (already converted to Timestamps in policyDoc)
                effectiveDate: (policyDoc['effectiveDate'] as Timestamp) || undefined,
                publishDate: (policyDoc['publishDate'] as Timestamp) || undefined,
                expiryDate: (policyDoc['expiryDate'] as Timestamp) || undefined,
                acknowledgmentDeadline: (policyDoc['acknowledgmentDeadline'] as Timestamp) || undefined,

                // Targets snapshot (built from NEW targets above)
                targets: targetsSnapshot,

                // Approval workflow snapshot (built from NEW workflow above)
                approvalWorkflow: workflowSnapshot,

                // Settings snapshot - capture the NEW values
                requiresAcknowledgment: formValue.requiresAcknowledgment || undefined,
                isMandatory: formValue.isMandatory || undefined,
            };

            // Remove undefined fields - Firebase doesn't accept undefined values
            const cleanRevision = this.removeUndefinedFields(revision) as PolicyRevision;
            policyDoc['revisions'] = [...(formValue.revisions || []), cleanRevision];
        }

        // If reinstating a deleted policy, clear deletion fields
        if (isReinstatement) {
            policyDoc['deletedAt'] = null;
            policyDoc['previousStatus'] = null;
            policyDoc['reinstatedAt'] = Timestamp.now();
        }

        // Save to Firestore
        try {
            await this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policies')
                .doc(policyKey)
                .set(policyDoc, { merge: true });

            const action = this.isEditMode ? 'updated' : 'created';
            this.snackbar.open(`Policy ${action} successfully (v${policyDoc['version']})`, 'Close', {
                duration: 3000,
            });
            this.resetForm();
            this.tabIndex = 1; // Go to list tab

            // Refresh the table
            this.refreshPoliciesList();
            return policyKey;
        } catch (error) {
            console.error('Error saving policy: ', error);
            this.snackbar.open('Error saving policy', 'Close', { duration: 3000 });
            return null;
        }
    }

    /**
     * Helper to save policy document to Firestore
     * @returns the saved policy key, or null if the write failed (see `executeSavePolicy`).
     */
    private async savePolicyToFirestore(
        policyKey: string,
        policyDoc: Record<string, unknown>,
        formValue: typeof this.form.value
    ): Promise<string | null> {
        // DEBUG: Log the document being saved
        console.log('📋 Saving policyDoc:', JSON.stringify(policyDoc, null, 2));

        // Save to Firestore
        try {
            await this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policies')
                .doc(policyKey)
                .set(policyDoc, { merge: true });

            this.snackbar.open(`Policy created successfully (v${policyDoc['version']})`, 'Close', {
                duration: 3000,
            });
            this.resetForm();
            this.tabIndex = 1; // Go to list tab

            // Refresh the table
            this.refreshPoliciesList();
            return policyKey;
        } catch (error) {
            console.error('Error saving policy: ', error);
            this.snackbar.open('Error saving policy', 'Close', { duration: 3000 });
            return null;
        }
    }

    public editPolicy(row: OrgPolicy): void {
        this.isEditMode = true;
        this.editingPolicyKey = row.key;
        this.editingPolicy = row; // Store full policy for reinstate
        this.isEditingDeletedPolicy = row.status === 'deleted';

        // Parse dates and extract times
        const effectiveDateObj = row.effectiveDate?.toDate?.() || null;
        const publishDateObj = row.publishDate?.toDate?.() || null;
        const expiryDateObj = row.expiryDate?.toDate?.() || null;
        const acknowledgmentDeadlineObj = row.acknowledgmentDeadline?.toDate?.() || null;

        const parsed = {
            key: row.key,
            name: row.name,
            description: row.description || null,
            category: row.category || 'general',
            customCategory: row.customCategory || null,
            // Use policyWriting if available, otherwise fall back to description for legacy policies
            // Convert plain text/markdown to HTML for TinyMCE
            policyWriting: this.convertToHtml(row.policyWriting || row.description || ''),
            version: row.version || 1, // Keep same version - only increment via "Create New Version" action
            status: row.status || 'draft',
            effectiveDate: effectiveDateObj,
            effectiveTime: this.extractTimeFromDate(effectiveDateObj),
            publishDate: publishDateObj,
            publishTime: this.extractTimeFromDate(publishDateObj),
            expiryDate: expiryDateObj,
            expiryTime: this.extractTimeFromDate(expiryDateObj),
            acknowledgmentDeadline: acknowledgmentDeadlineObj,
            acknowledgmentDeadlineTime: this.extractTimeFromDate(acknowledgmentDeadlineObj),
            requiresAcknowledgment: row.requiresAcknowledgment || false,
            isMandatory: row.isMandatory || false,
            targetMode: row.targets?.mode || 'all_organization',
            revisions: row.revisions || [],
            lastModifiedDate: row.modifiedOn?.toDate?.() || null,
        };

        // Load targets and approval workflow for editing
        this.selectedTargets = row.targets || null;
        this.approvalWorkflow = row.approvalWorkflow || null;

        // Update target summary if targets exist
        if (row.targets) {
            this.targetSummary = {
                partCount: row.targets.parts?.length || 0,
                hatCount: row.targets.directHats?.length || 0,
                userCount: row.targets.directUsers?.length || 0,
            };
        }

        // Update approval step count
        if (row.approvalWorkflow) {
            this.approvalStepCount = row.approvalWorkflow.steps?.length || 0;
        }

        this.form.reset();
        this.form.patchValue(parsed);

        // Lock fields for published policies (only expiry date/deadline editable)
        if (row.status === 'published') {
            this.lockFieldsForPublished();
        }

        // Reset editor state since it will be recreated when tab switches
        // The *ngIf="tabIndex === 0" destroys/recreates the editor on tab switch
        this.editorReady = false;
        this.editorInstance = null;

        // Store content for when editor initializes (editor is created fresh when tab becomes active)
        this.pendingEditorContent = parsed.policyWriting || '';

        this.tabIndex = 0;

        // Focus the policy name input after tab switch
        setTimeout(() => {
            this.policyNameInput?.nativeElement?.focus();
        }, 100);
    }

    /**
     * Lock form fields for published policies.
     * Only expiryDate, expiryTime, acknowledgmentDeadline, and acknowledgmentDeadlineTime remain enabled.
     */
    private lockFieldsForPublished(): void {
        const fieldsToDisable = [
            'name',
            'description',
            'category',
            'customCategory',
            'policyWriting',
            'effectiveDate',
            'effectiveTime',
            'publishDate',
            'publishTime',
            'requiresAcknowledgment',
            'isMandatory',
            'targetMode',
        ];
        fieldsToDisable.forEach((field) => {
            this.form.get(field)?.disable({ emitEvent: false });
        });
    }

    /**
     * Unlock all form fields (called when form is reset or when editing non-published policies).
     */
    private unlockAllFields(): void {
        Object.keys(this.form.controls).forEach((field) => {
            this.form.get(field)?.enable({ emitEvent: false });
        });
    }

    /**
     * Approve a policy - handles both direct approval and admin override
     */
    public async approvePolicy(policy: OrgPolicy, event?: Event): Promise<void> {
        if (event) {
            event.stopPropagation();
        }

        // Close menu before showing dialog
        this.closeAllMenus();

        const hasWorkflow = policy.approvalWorkflow && policy.approvalWorkflow.steps.length > 0;

        // Anything already mid-chain can only be short-circuited by an admin
        // override — the per-step decisions belong to the named approvers and
        // are taken in the details dialog. A workflow-less policy can reach
        // `pending_approval` only from before the cutover; override is the
        // server's only route out of it, so it lands here too.
        if (policy.status === 'pending_approval') {
            this.showAdminOverrideDialog(policy);
            return;
        }

        // If has workflow but not pending, can't approve yet
        if (hasWorkflow) {
            this.snackbar.open('This policy must be submitted for approval first.', 'Close', { duration: 4000 });
            return;
        }

        // No workflow - direct approval
        const confirmed = await this.dialogService.asyncConfirm(
            'Approve Policy?',
            `Are you sure you want to approve "${policy.name}"?\n\nThis will change the status to Approved.`
        );
        if (!confirmed) return;

        await this.executeApproval(policy, false);
    }

    /**
     * Show admin override dialog for policies with approval workflows
     */
    private showAdminOverrideDialog(policy: OrgPolicy): void {
        import('./policy-approval-override-dialog').then((module) => {
            const pendingApprovers = this.getPendingApproverNames(policy);

            const dialogRef = this.dialog.open(module.PolicyApprovalOverrideDialogComponent, {
                width: '500px',
                data: {
                    policyName: policy.name,
                    currentStatus: policy.status,
                    pendingApprovers,
                },
            });

            dialogRef.afterClosed().subscribe((result) => {
                if (result?.confirmed && result?.reason) {
                    this.executeApproval(policy, true, result.reason);
                }
            });
        });
    }

    /**
     * Get the names of pending approvers from the workflow
     */
    private getPendingApproverNames(policy: OrgPolicy): string[] {
        if (!policy.approvalWorkflow?.steps) return [];

        return policy.approvalWorkflow.steps
            .filter((step) => step.status !== 'approved')
            .map((step) => step.approver?.name || step.name || 'Unknown Approver');
    }

    /**
     * Execute the approval through the server (OE-75, design §2e).
     *
     * This used to write `status: 'approved'` (plus the audit entry and the
     * override stamp) straight to the policy doc, which meant the browser
     * decided the outcome. Both routes now go to `api-policyApproval`: a
     * workflow-less policy is auto-approved by `submit`, and an admin bypass
     * goes through `override`. The server writes the status, the audit entry
     * and the override record; the FE only reports the result.
     */
    private async executeApproval(policy: OrgPolicy, isAdminOverride: boolean, overrideReason?: string): Promise<void> {
        // Determine the expected status FIRST so we can track spinner correctly:
        // - If publishDate is in the future → 'scheduled'
        // - Otherwise → 'approved'
        let newStatus: 'approved' | 'scheduled' = 'approved';
        const publishDate = policy.publishDate?.toDate?.();
        if (publishDate && publishDate > new Date()) {
            newStatus = 'scheduled';
        }

        // Start spinner with expected status
        this.startProcessing(policy.key, newStatus);

        const target = {
            orgKey: this.data.organization.key,
            policyKey: policy.key,
            apiBaseUrl: this.data.apiBaseUrl,
        };

        const result = isAdminOverride
            ? await this.approvalService.override({ ...target, reason: overrideReason || '' })
            : await this.approvalService.submit(target);

        if (!result.ok) {
            console.error('Policy approval refused:', result.error, result.message);
            this.snackbar.open(result.message, 'Close', { duration: 5000 });
            // Clear spinner on refusal since status won't change
            this.stopProcessing();
            return;
        }

        const scheduled = (result.data?.status || newStatus) === 'scheduled';
        let message: string;
        if (scheduled) {
            message = publishDate
                ? `Policy "${policy.name}" approved and scheduled for ${publishDate.toLocaleDateString()}`
                : `Policy "${policy.name}" approved and scheduled`;
        } else {
            message = isAdminOverride
                ? `Policy "${policy.name}" approved via admin override`
                : `Policy "${policy.name}" approved successfully`;
        }
        this.snackbar.open(message, 'Close', { duration: 3000 });
        // Subscription will detect status change and clear spinner
    }

    /**
     * Check if policy can be approved directly
     * Shows approve button when:
     * - Status is 'draft' and no approval workflow configured
     * - Status is 'pending_approval' (ready to be approved)
     */
    public canApproveDirectly(policy: OrgPolicy): boolean {
        // Can approve if:
        // 1. Status is pending_approval (submitted for approval)
        // 2. Status is draft AND no approval workflow (can approve directly)
        if (policy.status === 'pending_approval') {
            return true;
        }

        if (policy.status === 'draft' && (!policy.approvalWorkflow || policy.approvalWorkflow.steps.length === 0)) {
            return true;
        }

        return false;
    }

    /**
     * Delete a policy (soft delete - marks as 'deleted' status)
     * If the policy was published, it will be unpublished first - clearing all assignments.
     */
    public async deletePolicy(policy: OrgPolicy, event?: Event): Promise<void> {
        if (event) {
            event.stopPropagation();
        }

        // Close menu before showing dialog
        this.closeAllMenus();

        const wasPublished = policy.status === 'published' || policy.status === 'scheduled';
        const hasAssignments = (policy.targets?.resolvedHatKeys?.length || 0) > 0;

        let warningTitle: string;
        let warningMessage: string;
        if (wasPublished || hasAssignments) {
            warningTitle = '⚠️ Delete Published Policy?';
            warningMessage =
                `This policy ${wasPublished ? 'is currently PUBLISHED and ' : ''}will be UNPUBLISHED.\n\n` +
                `• All user/hat assignments will be removed\n` +
                `• Users will no longer see this policy\n` +
                `• The policy can be reinstated later (but will need to be re-published)`;
        } else {
            warningTitle = `Delete "${policy.name}"?`;
            warningMessage = `The policy can be reinstated later if needed.`;
        }

        const confirmed = await this.dialogService.asyncConfirm(warningTitle, warningMessage);
        if (!confirmed) return;

        const now = Timestamp.now();
        const uid = await this.currentUid();

        // Build the update payload - MUST unpublish by clearing resolvedHatKeys
        const updateData: Record<string, any> = stampBlame(
            {
                status: 'deleted',
                modifiedOn: now,
                deletedAt: now,
                previousStatus: policy.status, // Store for potential reinstatement
                isPublished: false,
                // Clear all resolved targets - this "unpublishes" the policy
                'targets.resolvedHatKeys': [],
                'targets.resolvedHatCount': 0,
                'targets.resolvedAt': null,
            },
            uid
        );

        // If was published, record when it was unpublished
        if (wasPublished) {
            updateData['unpublishedAt'] = now;
            updateData['publishedAt'] = null; // Clear the published timestamp
        }

        this.db
            .collection('organizations')
            .doc(this.data.organization.key)
            .collection('policies')
            .doc(policy.key)
            .update(updateData)
            .then(() => {
                const message =
                    wasPublished || hasAssignments
                        ? `Policy "${policy.name}" unpublished and deleted`
                        : `Policy "${policy.name}" deleted`;
                this.snackbar.open(message, 'Close', { duration: 3000 });
                this.refreshPoliciesList();
            })
            .catch((error) => {
                console.error('Error deleting policy:', error);
                this.snackbar.open('Error deleting policy', 'Close', { duration: 3000 });
            });
    }

    /**
     * Reinstate a deleted policy back to its previous status (or draft if unknown)
     */
    public async reinstatePolicy(policy: OrgPolicy, event?: Event): Promise<void> {
        if (event) {
            event.stopPropagation();
        }

        // Close menu before showing dialog
        this.closeAllMenus();

        // Determine what status to restore to
        const previousStatus = (policy as any).previousStatus || 'draft';
        const restoreToStatus = (
            ['published', 'scheduled'].includes(previousStatus) ? 'draft' : previousStatus
        ) as PolicyStatus;

        const description =
            previousStatus === 'published'
                ? `The policy will be restored to "${restoreToStatus}" status.\n\n⚠️ Note: Previously published policies are restored as drafts and must be re-approved/published.`
                : `The policy will be restored to "${restoreToStatus}" status.`;

        const confirmed = await this.dialogService.asyncConfirm(`Reinstate "${policy.name}"?`, description);
        if (!confirmed) return;

        this.startProcessing(policy.key, restoreToStatus);

        try {
            const now = Timestamp.now();
            const uid = await this.currentUid();
            await this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policies')
                .doc(policy.key)
                .update(
                    stampBlame(
                        {
                            status: restoreToStatus,
                            modifiedOn: now,
                            reinstatedAt: now,
                            deletedAt: null, // Clear deletion timestamp
                            previousStatus: null, // Clear the previous status marker
                            // Clear resolution state so targets will be re-resolved when republished
                            'targets.resolvedAt': null,
                            'targets.resolvedHatKeys': [],
                            'targets.resolvedHatCount': 0,
                        },
                        uid
                    )
                );

            this.snackbar.open(`Policy "${policy.name}" reinstated as ${restoreToStatus}`, 'Close', { duration: 3000 });
            // If we're in edit mode viewing this policy, update the state
            if (this.editingPolicyKey === policy.key) {
                this.isEditingDeletedPolicy = false;
            }
            // Subscription will detect status change and clear spinner
        } catch (error) {
            console.error('Error reinstating policy:', error);
            this.snackbar.open('Error reinstating policy', 'Close', { duration: 3000 });
            this.stopProcessing();
        }
    }

    // =========================================================================
    // LIST VIEW ACTIONS - Actions available from the policy list menu
    // =========================================================================

    /**
     * Publish an approved or scheduled policy immediately from the list view
     */
    public async publishPolicyFromList(policy: OrgPolicy, event?: Event): Promise<void> {
        if (event) event.stopPropagation();

        // Close menu before showing dialog
        this.closeAllMenus();

        if (policy.status !== 'approved' && policy.status !== 'scheduled') {
            this.snackbar.open('Only approved or scheduled policies can be published', 'Close', { duration: 3000 });
            return;
        }

        const isScheduled = policy.status === 'scheduled';
        const confirmMessage = isScheduled
            ? `This policy is scheduled to publish on ${policy.publishDate?.toDate?.()?.toLocaleDateString() || 'a future date'}.\n\nPublish it immediately instead?`
            : `The policy will be immediately visible to all targeted users.`;

        const confirmed = await this.dialogService.asyncConfirm('Publish Policy Now?', confirmMessage);
        if (!confirmed) return;

        this.startProcessing(policy.key, 'published');
        try {
            // Use backend API if available to properly resolve target hats
            if (this.data.apiBaseUrl) {
                const url = `${this.data.apiBaseUrl}policyPublish-default`;
                const idToken = await this.getIdToken();
                const response = await firstValueFrom(
                    this.http.post<{ success: boolean; message: string; data?: { targetHatCount: number } }>(url, {
                        orgKey: this.data.organization.key,
                        policyKey: policy.key,
                        idToken,
                    })
                );

                if (response.success) {
                    const hatCount = response.data?.targetHatCount || 0;
                    this.snackbar.open(
                        `Policy "${policy.name}" published to ${hatCount} position${hatCount !== 1 ? 's' : ''}`,
                        'Close',
                        { duration: 4000 }
                    );
                    // Subscription will detect status change and clear spinner
                } else {
                    console.error('Policy publish failed:', response.message);
                    this.snackbar.open(`Error: ${response.message}`, 'Close', { duration: 4000 });
                    // Clear spinner on API error since status won't change
                    this.stopProcessing();
                }
            } else {
                // Fallback: Direct Firestore update (without target resolution)
                const now = Timestamp.now();
                const uid = await this.currentUid();
                await this.db
                    .collection('organizations')
                    .doc(this.data.organization.key)
                    .collection('policies')
                    .doc(policy.key)
                    .update(
                        stampBlame(
                            {
                                status: 'published',
                                publishedAt: now,
                                modifiedOn: now,
                            },
                            uid
                        )
                    );
                this.snackbar.open(`Policy "${policy.name}" published`, 'Close', { duration: 3000 });
                // Subscription will detect status change and clear spinner
            }
        } catch (error) {
            console.error('Error publishing policy:', error);
            this.snackbar.open('Error publishing policy', 'Close', { duration: 3000 });
            // Clear spinner on error since status won't change
            this.stopProcessing();
        }
    }

    /**
     * Cancel a policy before it's published (from list view)
     */
    public async cancelPolicy(policy: OrgPolicy, event?: Event): Promise<void> {
        if (event) event.stopPropagation();

        // Close menu before showing dialog
        this.closeAllMenus();

        if (!['approved', 'scheduled'].includes(policy.status)) {
            this.snackbar.open('Only approved or scheduled policies can be cancelled', 'Close', { duration: 3000 });
            return;
        }

        const confirmed = await this.dialogService.asyncConfirm(
            `Cancel "${policy.name}"?`,
            `This will prevent the policy from being published.\n\nThe policy can be reinstated later if needed.`
        );
        if (!confirmed) return;

        this.startProcessing(policy.key, 'cancelled');
        try {
            const now = Timestamp.now();
            const uid = await this.currentUid();
            await this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policies')
                .doc(policy.key)
                .update(
                    stampBlame(
                        {
                            status: 'cancelled',
                            cancelledAt: now,
                            modifiedOn: now,
                            previousStatus: policy.status,
                        },
                        uid
                    )
                );
            this.snackbar.open(`Policy "${policy.name}" cancelled`, 'Close', { duration: 3000 });
            // Subscription will detect status change and clear spinner
        } catch (error) {
            console.error('Error cancelling policy:', error);
            this.snackbar.open('Error cancelling policy', 'Close', { duration: 3000 });
            // Clear spinner on error since status won't change
            this.stopProcessing();
        }
    }

    /**
     * Revoke a published policy (from list view)
     */
    public async revokePolicy(policy: OrgPolicy, event?: Event): Promise<void> {
        if (event) event.stopPropagation();

        // Close menu before showing dialog
        this.closeAllMenus();

        if (policy.status !== 'published') {
            this.snackbar.open('Only published policies can be revoked', 'Close', { duration: 3000 });
            return;
        }

        const result = await this.dialogService.asyncPrompt({
            title: `Revoke "${policy.name}"?`,
            description:
                'This will immediately remove the policy from all users.\n\nPlease enter a reason for revocation:',
            inputLabel: 'Reason for revocation',
            inputPlaceholder: 'e.g., Policy superseded by new version',
            inputRequired: false,
            confirmButtonText: 'Revoke',
            confirmButtonColor: 'warn',
        });

        if (!result?.confirmed) return;

        this.startProcessing(policy.key, 'revoked');
        try {
            const now = Timestamp.now();
            const uid = await this.currentUid();
            await this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policies')
                .doc(policy.key)
                .update(
                    stampBlame(
                        {
                            status: 'revoked',
                            revokedAt: now,
                            revocationReason: result.value || 'No reason provided',
                            modifiedOn: now,
                            previousStatus: policy.status,
                            // Clear assignments and resolution state so policy can be re-resolved when republished
                            'targets.resolvedHatKeys': [],
                            'targets.resolvedHatCount': 0,
                            'targets.resolvedAt': null,
                        },
                        uid
                    )
                );
            this.snackbar.open(`Policy "${policy.name}" revoked`, 'Close', { duration: 3000 });
            // Subscription will detect status change and clear spinner
        } catch (error) {
            console.error('Error revoking policy:', error);
            this.snackbar.open('Error revoking policy', 'Close', { duration: 3000 });
            // Clear spinner on error since status won't change
            this.stopProcessing();
        }
    }

    /**
     * Archive a policy (from list view)
     */
    public async archivePolicy(policy: OrgPolicy, event?: Event): Promise<void> {
        if (event) event.stopPropagation();

        // Close menu before showing dialog
        this.closeAllMenus();

        const allowedStatuses = ['published', 'expired', 'cancelled', 'revoked'];
        if (!allowedStatuses.includes(policy.status)) {
            this.snackbar.open('This policy cannot be archived in its current status', 'Close', { duration: 3000 });
            return;
        }

        const confirmed = await this.dialogService.asyncConfirm(
            `Archive "${policy.name}"?`,
            `The policy will be moved to archived status.\n\nIt can still be viewed and a new version can be created.`
        );
        if (!confirmed) return;

        this.startProcessing(policy.key, 'archived');

        try {
            const now = Timestamp.now();
            const uid = await this.currentUid();
            const updateData: Record<string, any> = stampBlame(
                {
                    status: 'archived',
                    archivedAt: now,
                    modifiedOn: now,
                    previousStatus: policy.status,
                },
                uid
            );

            // If was published, clear assignments and resolution state so policy can be re-resolved if republished
            if (policy.status === 'published') {
                updateData['targets.resolvedHatKeys'] = [];
                updateData['targets.resolvedHatCount'] = 0;
                updateData['targets.resolvedAt'] = null;
            }

            await this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policies')
                .doc(policy.key)
                .update(updateData);

            this.snackbar.open(`Policy "${policy.name}" archived`, 'Close', { duration: 3000 });
            // Subscription will detect status change and clear spinner
        } catch (error) {
            console.error('Error archiving policy:', error);
            this.snackbar.open('Error archiving policy', 'Close', { duration: 3000 });
            this.stopProcessing();
        }
    }

    /**
     * Republish an expired policy from the list view
     */
    public async republishPolicyFromList(policy: OrgPolicy, event?: Event): Promise<void> {
        if (event) event.stopPropagation();

        // Close menu before showing dialog
        this.closeAllMenus();

        if (policy.status !== 'expired') {
            this.snackbar.open('Only expired policies can be republished', 'Close', { duration: 3000 });
            return;
        }

        // Check if has expiry date
        const hasExpiry = !!policy.expiryDate;
        let confirmTitle: string;
        let confirmMessage: string;

        if (hasExpiry) {
            confirmTitle = `Republish "${policy.name}"?`;
            confirmMessage = `⚠️ This policy has an expiry date that has passed.\n\nClick Confirm to REMOVE the expiry date and republish.\nClick Cancel to edit the policy and set a new expiry date first.`;
        } else {
            confirmTitle = `Republish "${policy.name}"?`;
            confirmMessage = `The policy will be set back to published status.`;
        }

        const confirmed = await this.dialogService.asyncConfirm(confirmTitle, confirmMessage);
        if (!confirmed) return;

        // Use backend API if available to properly resolve target hats
        if (this.data.apiBaseUrl) {
            try {
                const url = `${this.data.apiBaseUrl}policyPublish-default`;
                const idToken = await this.getIdToken();
                const response = await firstValueFrom(
                    this.http.post<{ success: boolean; message: string; data?: { targetHatCount: number } }>(url, {
                        orgKey: this.data.organization.key,
                        policyKey: policy.key,
                        clearExpiryDate: hasExpiry, // Clear expiry if it existed
                        idToken,
                    })
                );

                if (response.success) {
                    const hatCount = response.data?.targetHatCount || 0;
                    this.snackbar.open(
                        `Policy "${policy.name}" republished to ${hatCount} position${hatCount !== 1 ? 's' : ''}`,
                        'Close',
                        { duration: 4000 }
                    );
                    this.refreshPoliciesList();
                } else {
                    console.error('Policy republish failed:', response.message);
                    this.snackbar.open(`Error: ${response.message}`, 'Close', { duration: 4000 });
                }
            } catch (error) {
                console.error('Error republishing policy via API:', error);
                this.snackbar.open('Error republishing policy', 'Close', { duration: 3000 });
            }
        } else {
            // Fallback: Direct Firestore update - triggers backend target resolution
            const now = Timestamp.now();
            const uid = await this.currentUid();
            const updateData: Record<string, any> = stampBlame(
                {
                    status: 'published',
                    publishedAt: now,
                    modifiedOn: now,
                    // Clear resolution state so backend trigger will re-resolve targets
                    'targets.resolvedAt': null,
                },
                uid
            );

            // Remove expiry if it existed
            if (hasExpiry) {
                updateData['expiryDate'] = null;
            }

            this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policies')
                .doc(policy.key)
                .update(updateData)
                .then(() => {
                    this.snackbar.open(`Policy "${policy.name}" republished`, 'Close', { duration: 3000 });
                    this.refreshPoliciesList();
                })
                .catch((error) => {
                    console.error('Error republishing policy:', error);
                    this.snackbar.open('Error republishing policy', 'Close', { duration: 3000 });
                });
        }
    }

    /**
     * Create a new version of a policy from the list view
     * Opens the edit form with the policy data but as a new document
     */
    public async createNewVersionFromList(policy: OrgPolicy, event?: Event): Promise<void> {
        if (event) event.stopPropagation();

        // Close menu before showing dialog
        this.closeAllMenus();

        const allowedStatuses = ['published', 'expired', 'archived', 'deleted', 'cancelled', 'revoked'];
        if (!allowedStatuses.includes(policy.status)) {
            this.snackbar.open('Cannot create new version from this status', 'Close', { duration: 3000 });
            return;
        }

        const newVersion = (policy.version || 1) + 1;
        const requiresAck = policy.requiresAcknowledgment === true;

        // Build confirmation message
        let confirmMessage = `The current version will remain unchanged.\n\nA new draft will be created for editing.`;
        if (requiresAck) {
            confirmMessage += `\n\n⚠️ This policy requires acknowledgment. The acknowledgment deadline will be cleared and you'll need to set a new deadline before publishing.`;
        }

        const confirmed = await this.dialogService.asyncConfirm(`Create New Version (v${newVersion})?`, confirmMessage);
        if (!confirmed) return;

        // Load the policy into edit mode, but clear the key to create new
        this.editPolicy(policy);

        // After loading, override to create new version
        setTimeout(() => {
            const patchValues: any = {
                key: null,
                version: newVersion,
                status: 'draft' as PolicyStatus,
            };

            // Clear acknowledgment deadline for new version - user must set a new one
            if (requiresAck) {
                patchValues.acknowledgmentDeadline = null;
                patchValues.acknowledgmentDeadlineTime = null;
            }

            this.form.patchValue(patchValues);
            this.editingPolicyKey = ''; // Clear so it creates new
            this.isEditMode = false; // Treat as new policy
            this.tabIndex = 0; // Go to edit tab

            // Notify user with appropriate message
            if (requiresAck) {
                this.snackbar.open(
                    `Creating new draft v${newVersion}. Please set a new acknowledgment deadline.`,
                    'Close',
                    { duration: 5000 }
                );
            } else {
                this.snackbar.open(`Creating new draft version (v${newVersion})`, 'Close', { duration: 3000 });
            }
        }, 100);
    }

    /**
     * Publish an approved policy immediately
     */
    public async publishPolicy(): Promise<void> {
        if (!this.editingPolicyKey) return;

        const confirmed = await this.dialogService.asyncConfirm(
            'Publish this policy now?',
            `The policy will be immediately visible to all targeted users.`
        );
        if (!confirmed) return;

        // Use backend API if available to properly resolve target hats
        if (this.data.apiBaseUrl) {
            try {
                const url = `${this.data.apiBaseUrl}policyPublish-default`;
                const idToken = await this.getIdToken();
                const response = await firstValueFrom(
                    this.http.post<{ success: boolean; message: string; data?: { targetHatCount: number } }>(url, {
                        orgKey: this.data.organization.key,
                        policyKey: this.editingPolicyKey,
                        idToken,
                    })
                );

                if (response.success) {
                    const hatCount = response.data?.targetHatCount || 0;
                    this.snackbar.open(
                        `Policy published to ${hatCount} position${hatCount !== 1 ? 's' : ''}`,
                        'Close',
                        { duration: 4000 }
                    );
                    this.refreshPoliciesList();
                    // Update form to reflect new status
                    this.form.patchValue({ status: 'published' as PolicyStatus });
                } else {
                    console.error('Policy publish failed:', response.message);
                    this.snackbar.open(`Error: ${response.message}`, 'Close', { duration: 4000 });
                }
            } catch (error) {
                console.error('Error publishing policy via API:', error);
                this.snackbar.open('Error publishing policy', 'Close', { duration: 3000 });
            }
        } else {
            // Fallback: Direct save (without target resolution)
            this.form.patchValue({ status: 'published' as PolicyStatus });
            this.executeSavePolicy();
            this.snackbar.open('Policy published successfully', 'Close', { duration: 3000 });
        }
    }

    /**
     * Enter edit mode to create a new draft version of a published/expired/archived policy.
     * This enables all form fields for editing. User saves via "Save New Version" button.
     */
    public async createNewVersion(): Promise<void> {
        if (!this.editingPolicyKey || !this.editingPolicy) return;

        // If already editing for new version, save it
        if (this.isEditingForNewVersion) {
            this.saveNewVersion();
            return;
        }

        const currentVersion = this.form.get('version')?.value || 1;

        // Store original policy for cancel functionality
        this.originalPolicyBeforeNewVersion = { ...this.editingPolicy };

        // Enter editing mode for new version
        this.isEditingForNewVersion = true;

        // Unlock all form fields for editing
        this.unlockAllFields();

        // Set version to next and status to draft (in form only, not saved yet)
        this.form.patchValue({
            version: currentVersion + 1,
            status: 'draft' as PolicyStatus,
        });

        // Clear acknowledgment deadline for new version if policy requires acknowledgment
        if (this.form.get('requiresAcknowledgment')?.value === true) {
            this.form.patchValue({
                acknowledgmentDeadline: null,
                acknowledgmentDeadlineTime: null,
            });
        }

        this.snackbar.open(`Editing v${currentVersion + 1}. Make your changes and click "Save New Version".`, 'Close', {
            duration: 4000,
        });
    }

    /**
     * Save the new version being edited.
     * Creates a new policy document with incremented version.
     */
    public saveNewVersion(): void {
        if (!this.isEditingForNewVersion) return;

        const newVersion = this.form.get('version')?.value || 2;
        const requiresAck = this.form.get('requiresAcknowledgment')?.value === true;

        // Reset key to create a new document
        this.form.patchValue({ key: null });
        this.editingPolicyKey = ''; // Clear so it creates new
        this.isEditMode = false; // Treat as new policy for save

        this.executeSavePolicy();

        // Reset new version editing state
        this.isEditingForNewVersion = false;
        this.originalPolicyBeforeNewVersion = null;

        // Notify user with appropriate message
        if (requiresAck) {
            this.snackbar.open(`New draft v${newVersion} created. Please set a new acknowledgment deadline.`, 'Close', {
                duration: 5000,
            });
        } else {
            this.snackbar.open(`New draft version (v${newVersion}) created`, 'Close', { duration: 3000 });
        }
    }

    /**
     * Republish an expired policy
     * Prompts user to handle expiry date before republishing
     */
    public async republishPolicy(): Promise<void> {
        if (!this.editingPolicyKey) return;

        const currentExpiryDate = this.form.get('expiryDate')?.value;
        let clearExpiryDate = false;

        if (currentExpiryDate) {
            // Policy has an expiry date - prompt user to handle it
            const choice = await this.dialogService.asyncConfirm(
                'Expiry Date Has Passed',
                `This policy has an expiry date that has passed.\n\n` +
                    `• Click Confirm to REMOVE the expiry date and republish (policy won't expire)\n` +
                    `• Click Cancel to go back and set a new expiry date first\n\n` +
                    `Tip: Update the expiry date in the form before clicking Republish.`
            );

            if (!choice) {
                // User wants to set a new date - don't republish yet
                this.snackbar.open('Update the expiry date and click Republish again', 'Close', { duration: 4000 });
                return;
            }

            clearExpiryDate = true;
        } else {
            // No expiry date - just republish
            const confirmed = await this.dialogService.asyncConfirm(
                'Republish this policy?',
                `The policy will be set back to published status.`
            );
            if (!confirmed) return;
        }

        // Use backend API if available to properly resolve target hats
        if (this.data.apiBaseUrl) {
            try {
                const url = `${this.data.apiBaseUrl}policyPublish-default`;
                const idToken = await this.getIdToken();
                const response = await firstValueFrom(
                    this.http.post<{ success: boolean; message: string; data?: { targetHatCount: number } }>(url, {
                        orgKey: this.data.organization.key,
                        policyKey: this.editingPolicyKey,
                        clearExpiryDate: clearExpiryDate,
                        idToken,
                    })
                );

                if (response.success) {
                    const hatCount = response.data?.targetHatCount || 0;
                    this.snackbar.open(
                        `Policy republished to ${hatCount} position${hatCount !== 1 ? 's' : ''}`,
                        'Close',
                        { duration: 4000 }
                    );
                    this.refreshPoliciesList();
                    // Update form to reflect new status
                    this.form.patchValue({
                        status: 'published' as PolicyStatus,
                        expiryDate: clearExpiryDate ? null : this.form.get('expiryDate')?.value,
                        expiryTime: clearExpiryDate ? null : this.form.get('expiryTime')?.value,
                    });
                } else {
                    console.error('Policy republish failed:', response.message);
                    this.snackbar.open(`Error: ${response.message}`, 'Close', { duration: 4000 });
                }
            } catch (error) {
                console.error('Error republishing policy via API:', error);
                this.snackbar.open('Error republishing policy', 'Close', { duration: 3000 });
            }
        } else {
            // Fallback: Direct save (without target resolution)
            if (clearExpiryDate) {
                this.form.patchValue({
                    expiryDate: null,
                    expiryTime: null,
                    status: 'published' as PolicyStatus,
                });
            } else {
                this.form.patchValue({ status: 'published' as PolicyStatus });
            }

            this.executeSavePolicy();
            this.snackbar.open('Policy republished successfully', 'Close', { duration: 3000 });
        }
    }

    /**
     * Reinstate a cancelled or revoked policy back to draft (from edit form)
     * Different from reinstatePolicy() which is for list view
     * Different from reinstateAndSave() which is for deleted status
     */
    public async reinstateCancelledOrRevoked(): Promise<void> {
        if (!this.editingPolicyKey) return;

        const currentStatus = this.editingPolicy?.status;
        const statusLabel = currentStatus === 'cancelled' ? 'cancelled' : 'revoked';

        const confirmed = await this.dialogService.asyncConfirm(
            `Reinstate ${statusLabel} policy?`,
            `The policy will be restored to DRAFT status.\n\nYou can then edit it and submit for approval again.`
        );
        if (!confirmed) return;

        this.form.patchValue({ status: 'draft' as PolicyStatus });
        this.executeSavePolicy();
        this.snackbar.open('Policy reinstated as draft', 'Close', { duration: 3000 });
    }

    /**
     * Reinstate a deleted policy and save any edits made
     * Used from the edit form when editing a deleted policy
     *
     * Reinstating ALWAYS goes to 'draft' status.
     * The user can then review and click "Submit for Approval" which will:
     * - Go to 'pending_approval' if approval workflow exists
     * - Go to 'approved' or 'scheduled' if no approval workflow
     */
    public async reinstateAndSave(): Promise<void> {
        if (!this.editingPolicyKey || !this.isEditingDeletedPolicy) {
            this.savePolicy();
            return;
        }

        const hasApprovalWorkflow = this.approvalWorkflow && this.approvalWorkflow.steps.length > 0;

        let statusMessage: string;
        if (hasApprovalWorkflow) {
            statusMessage =
                'The policy will be restored as a DRAFT.\n\nYou can then submit it for approval through the configured workflow.';
        } else {
            statusMessage =
                'The policy will be restored as a DRAFT.\n\nSince no approval workflow is configured, you can submit it directly for approval.';
        }

        const confirmed = await this.dialogService.asyncConfirm('Reinstate this policy?', statusMessage);
        if (!confirmed) return;

        // Reinstating always goes to draft - user must explicitly submit for approval
        this.form.patchValue({ status: 'draft' as PolicyStatus });

        // Mark that we're no longer editing a deleted policy
        this.isEditingDeletedPolicy = false;

        // Now save the policy - executeSavePolicy will handle the update
        this.executeSavePolicy(true); // Pass flag to indicate this is a reinstatement
    }

    public viewPolicyDetails(row: OrgPolicy): void {
        // Import and open the details dialog
        import('../org-policy-details-dialog').then((module) => {
            this.dialog.open(module.OrgPolicyDetailsDialogComponent, {
                width: '85vw',
                height: '85vh',
                maxWidth: '1400px',
                maxHeight: '900px',
                data: {
                    policy: row,
                    policyKey: row.key,
                    organization: this.data.organization,
                },
                panelClass: 'policy-details-dialog',
            });
        });
    }

    public applyFilter(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value || '';
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    public resetForm(): void {
        // Re-enable all form controls before reset (may have been locked for published policy)
        this.unlockAllFields();

        this.form.reset({
            category: 'general',
            version: 1,
            status: 'draft',
            targetMode: 'all_organization',
            requiresAcknowledgment: false,
            isMandatory: false,
        });
        this.isEditMode = false;
        this.editingPolicyKey = null;
        this.editingPolicy = null;
        this.isEditingDeletedPolicy = false;

        // Reset targets and approval workflow state
        this.selectedTargets = null;
        this.targetSummary = { partCount: 0, hatCount: 0, userCount: 0 };
        this.approvalWorkflow = null;
        this.approvalStepCount = 0;
        this.isEditingForNewVersion = false;
        this.originalPolicyBeforeNewVersion = null;
    }

    public cancelEdit(): void {
        // If editing for new version, restore original policy instead of full reset
        if (this.isEditingForNewVersion && this.originalPolicyBeforeNewVersion) {
            this.isEditingForNewVersion = false;
            this.editPolicy(this.originalPolicyBeforeNewVersion);
            this.originalPolicyBeforeNewVersion = null;
            this.snackbar.open('New version editing cancelled', 'Close', { duration: 2000 });
            return;
        }

        this.resetForm();
        this.tabIndex = 1;
    }

    /**
     * Open AI assistance dialog
     * If text is selected in the editor, opens in "Refine Selection" mode
     */
    public openAiAssistance(): void {
        // Check if there's a text selection in the TinyMCE editor
        let selectedText: string | undefined;
        let fullDocument: string | undefined;

        if (this.editorReady && this.editorInstance) {
            const selection = this.editorInstance.selection.getContent({ format: 'html' });
            if (selection && selection.trim().length > 0) {
                selectedText = selection;
                fullDocument = this.editorInstance.getContent();
            }
        }

        const dialogRef = this.dialog.open(PolicyAiDialogComponent, {
            width: '80%',
            height: '90vh',
            maxWidth: '1200px',
            maxHeight: '900px',
            data: {
                existingPolicy: this.form.get('policyWriting')?.value,
                companyName: this.data.organization?.name || 'Your Organization',
                selectedText,
                fullDocument,
            },
            panelClass: 'policy-ai-dialog',
        });

        dialogRef.afterClosed().subscribe((result: PolicyAiDialogResult | undefined) => {
            if (result) {
                if (result.isSelectionRefinement && this.editorReady && this.editorInstance) {
                    // Replace only the selected text with the refined content
                    this.editorInstance.selection.setContent(result.content);

                    // Update form value from editor
                    this.form.patchValue({ policyWriting: this.editorInstance.getContent() });

                    this.snackbar.open('Selection refined with AI content', 'Close', { duration: 3000 });
                } else {
                    // Replace entire content (existing behavior)
                    this.form.patchValue({ policyWriting: result.content });

                    if (this.editorReady && this.editorInstance) {
                        this.editorInstance.setContent(result.content);
                    }

                    this.snackbar.open('AI-generated content added to policy', 'Close', { duration: 3000 });
                }

                // Mark form as dirty so Save button becomes enabled
                this.form.markAsDirty();
            }
        });
    }

    /**
     * Submit policy for approval (OE-75, design §2e).
     *
     * The status transition is the SERVER's to make: this used to patch
     * `status: 'pending_approval'` into the save payload, which both bypassed
     * the chain (nothing resolved or notified the first approver) and is what
     * the §2f rules clause refuses. The edits are saved as they stand — a
     * draft — and `api-policyApproval` starts the chain from there.
     */
    public async submitForApproval(): Promise<void> {
        if (this.form.invalid) {
            this.snackbar.open('Please complete all required fields before submitting', 'Close', { duration: 3000 });
            return;
        }

        // A rejected chain re-enters through `resubmit`, which records the
        // resubmission rather than reading as a first-time submit. Read it
        // BEFORE saving — a successful save resets the form and clears the
        // in-memory workflow.
        const isResubmission =
            this.approvalWorkflow?.workflowStatus === 'rejected' && this.approvalWorkflow?.canResubmit === true;

        // Persist the pending edits first: the endpoint reads the STORED policy
        // (approvers included), so unsaved changes would not be part of the run.
        const policyKey = await this.executeSavePolicy();
        if (!policyKey) return; // save failed — executeSavePolicy already reported it

        const target = {
            orgKey: this.data.organization.key,
            policyKey,
            apiBaseUrl: this.data.apiBaseUrl,
        };

        const result = isResubmission
            ? await this.approvalService.resubmit(target)
            : await this.approvalService.submit(target);

        if (!result.ok) {
            console.error('Policy submit refused:', result.error, result.message);
            this.snackbar.open(result.message, 'Close', { duration: 5000 });
            return;
        }

        const awaiting = result.data?.status === 'pending_approval';
        this.snackbar.open(
            awaiting
                ? 'Submitted for approval — the first approver has been notified.'
                : result.message || 'Policy submitted.',
            'Close',
            { duration: 4000 }
        );
    }

    /**
     * Refresh the policies list from org doc's orgPolicies map with real-time updates
     * This follows the same pattern as orgUsers, orgHats, etc.
     */
    private refreshPoliciesList(): void {
        const orgKey = this.data.organization.key;
        console.log('📋 Subscribing to real-time orgPolicies updates for org:', orgKey);

        // Unsubscribe from previous subscription if any
        if (this.policiesSubscription) {
            this.policiesSubscription.unsubscribe();
        }

        // Subscribe to org doc updates to get orgPolicies changes
        this.policiesSubscription = this.db
            .collection('organizations')
            .doc(orgKey)
            .valueChanges()
            .subscribe({
                next: (orgDoc: any) => {
                    // Update the organization data with fresh orgHats for getResolvedHatCount()
                    if (orgDoc?.orgHats) {
                        this.data.organization.orgHats = orgDoc.orgHats;
                    }

                    const orgPolicies = orgDoc?.orgPolicies;
                    if (!orgPolicies) {
                        console.log('📋 No orgPolicies on org doc');
                        this.allPolicies = [];
                        this.filterByStatus(this.activeStatusFilter);
                        return;
                    }

                    const policiesKeys = orgPolicies.policiesKeys || [];
                    console.log('📋 Real-time orgPolicies update:', policiesKeys.length, 'policies');

                    // Map policy keys to policy objects from the orgPolicies map
                    const newTableData: OrgPolicy[] = policiesKeys
                        .map((policyKey: string) => {
                            const policy = orgPolicies[policyKey];
                            if (!policy || typeof policy !== 'object' || !policy.name) {
                                return null;
                            }
                            // Ensure key is set and parse timestamps
                            return parseEmbeddedTimestampsToFirebaseDate(
                                deepCopy({
                                    ...policy,
                                    key: policyKey,
                                })
                            );
                        })
                        .filter((policy: OrgPolicy | null): policy is OrgPolicy => policy !== null);

                    // Store all policies for filtering
                    this.allPolicies = newTableData;
                    // Apply current filter or show all
                    this.filterByStatus(this.activeStatusFilter);

                    // Set custom filter predicate for cross-entity search
                    this.dataSource.filterPredicate = (policy: OrgPolicy, filter: string) => {
                        if (!filter) return true;
                        const term = filter.toLowerCase();

                        // Match on policy name
                        if ((policy.name || '').toLowerCase().includes(term)) return true;
                        // Match on description
                        if ((policy.description || '').toLowerCase().includes(term)) return true;
                        // Match on status
                        if ((policy.status || '').toLowerCase().includes(term)) return true;
                        // Match on category
                        if ((policy.category || '').toLowerCase().includes(term)) return true;

                        // Match on hat names and user names via resolvedHatKeys
                        const hatKeys = policy.targets?.resolvedHatKeys || [];
                        const orgHats = this.data.organization?.orgHats;
                        if (orgHats) {
                            for (const hatKey of hatKeys) {
                                const hat = orgHats[hatKey] as any;
                                if (!hat) continue;
                                if ((hat.name || '').toLowerCase().includes(term)) return true;
                                const userKeys: string[] = hat.assignedToArray || hat.userKeys || [];
                                const orgUsers = this.data.organization?.orgUsers;
                                if (
                                    orgUsers &&
                                    userKeys.some((uk) =>
                                        ((orgUsers[uk]?.name || orgUsers[uk]?.userName || '') as string)
                                            .toLowerCase()
                                            .includes(term)
                                    )
                                )
                                    return true;
                            }
                        }

                        return false;
                    };

                    // Check if a pending processing action has completed
                    this.checkProcessingComplete(newTableData);
                },
                error: (err) => {
                    console.error('📋 Error in orgPolicies subscription:', err);
                },
            });
    }

    /**
     * Open the Policy Explorer dialog showing hierarchical views of policies
     */
    public openPolicyExplorer(): void {
        import('../org-policy-explorer-dialog/org-policy-explorer-dialog').then((module) => {
            this.dialog.open(module.OrgPolicyExplorerDialogComponent, {
                width: '90%',
                height: '90vh',
                maxWidth: '1400px',
                maxHeight: '900px',
                data: {
                    organization: this.data.organization,
                },
            });
        });
    }

    public ngOnDestroy(): void {
        if (this.policiesSubscription) {
            this.policiesSubscription.unsubscribe();
            this.policiesSubscription = null;
        }

        // Cleanup scroll handler
        this.cleanupScrollHandler();
    }

    /**
     * Setup scroll handler to close TinyMCE toolbar menus when scrolling
     * This fixes the issue where the "more" (3 dots) submenu stays stuck in place
     */
    private setupScrollHandler(): void {
        // Find the scrollable policy-form container
        const scrollContainer = document.querySelector('.policy-form');
        if (scrollContainer && this.editorInstance) {
            this.scrollHandler = () => {
                // Close any open TinyMCE toolbar menus/dropdowns
                if (this.editorInstance) {
                    // Fire a custom event to close any open menus
                    this.editorInstance.fire('ScrollContent');
                    // Also try to close any floating panels
                    const openMenus = document.querySelectorAll('.tox-tbtn--enabled, .tox-split-button--open');
                    openMenus.forEach((menu: Element) => {
                        // Simulate a click outside to close the menu
                        const htmlMenu = menu as HTMLElement;
                        if (
                            htmlMenu.classList.contains('tox-tbtn--enabled') ||
                            htmlMenu.classList.contains('tox-split-button--open')
                        ) {
                            // Trigger blur/close by dispatching escape key
                            const escEvent = new KeyboardEvent('keydown', {
                                key: 'Escape',
                                keyCode: 27,
                                bubbles: true,
                            });
                            document.dispatchEvent(escEvent);
                        }
                    });
                }
            };
            scrollContainer.addEventListener('scroll', this.scrollHandler, { passive: true });
        }
    }

    /**
     * Cleanup scroll handler on component destroy
     */
    private cleanupScrollHandler(): void {
        if (this.scrollHandler) {
            const scrollContainer = document.querySelector('.policy-form');
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', this.scrollHandler);
            }
            this.scrollHandler = null;
        }
    }

    /**
     * Open target selection dialog (Phase 3)
     */
    public openTargetSelection(): void {
        // Get the current target mode from the form
        const currentTargetMode = this.form.get('targetMode')?.value || 'all_organization';

        // Build current targets object, using form's targetMode as the source of truth
        const currentTargets: PolicyTargets | undefined = this.selectedTargets
            ? { ...this.selectedTargets, mode: currentTargetMode }
            : currentTargetMode === 'selected'
              ? { mode: 'selected', parts: [], directHats: [], directUsers: [] }
              : undefined;

        const dialogInput: PolicyTargetSelectorDialogInput = {
            organization: this.data.organization,
            currentTargets: currentTargets,
            mode: 'edit',
        };

        this.dialog
            .open<OrgPolicyTargetSelectorComponent, PolicyTargetSelectorDialogInput, PolicyTargetSelectorDialogResult>(
                OrgPolicyTargetSelectorComponent,
                {
                    data: dialogInput,
                    height: '80vh',
                    width: '85%',
                    maxHeight: '750px',
                    maxWidth: '950px',
                    panelClass: 'target-selector-panel',
                    autoFocus: false,
                }
            )
            .afterClosed()
            .pipe(take(1))
            .subscribe((result: PolicyTargetSelectorDialogResult | undefined) => {
                if (result) {
                    this.selectedTargets = result.targets;
                    this.targetSummary = result.summary;
                    this.form.patchValue({
                        targetMode: result.targets.mode,
                    });

                    // Mark form as dirty so Save button activates
                    this.form.markAsDirty();

                    this.snackbar.open(
                        `Selected ${result.summary.partCount} parts, ${result.summary.userCount} users`,
                        'Close',
                        { duration: 3000 }
                    );
                }
            });
    }

    /**
     * Open approvers selection dialog (Phase 4)
     */
    public openApproversSelection(): void {
        const dialogInput: PolicyApproversSelectorDialogInput = {
            organization: this.data.organization,
            currentWorkflow: this.approvalWorkflow || undefined,
            mode: 'edit',
        };

        this.dialog
            .open<
                OrgPolicyApproversSelectorComponent,
                PolicyApproversSelectorDialogInput,
                PolicyApproversSelectorDialogResult
            >(OrgPolicyApproversSelectorComponent, {
                data: dialogInput,
                height: '85vh',
                width: '80%',
                maxHeight: '800px',
                maxWidth: '900px',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result: PolicyApproversSelectorDialogResult | undefined) => {
                if (result) {
                    this.approvalWorkflow = result.workflow;
                    this.approvalStepCount = result.workflow.steps.length;

                    // Mark form as dirty so Save Changes button activates
                    this.form.markAsDirty();

                    const stepCount = this.approvalStepCount;
                    if (stepCount > 0) {
                        this.snackbar.open(
                            `Configured ${stepCount} approval step${stepCount > 1 ? 's' : ''}`,
                            'Close',
                            { duration: 3000 }
                        );
                    } else {
                        this.snackbar.open('No approval required - policy can be published directly', 'Close', {
                            duration: 3000,
                        });
                    }
                }
            });
    }

    /**
     * Converts plain text content to HTML for TinyMCE editor.
     * Detects if content is already HTML or plain text and converts accordingly.
     */
    private convertToHtml(content: string): string {
        if (!content) return '';

        // Check if content already contains HTML tags
        const hasHtmlTags = /<[a-z][\s\S]*>/i.test(content);
        if (hasHtmlTags) {
            return content; // Already HTML, return as-is
        }

        // Convert plain text to HTML
        let html = content;

        // Convert markdown-style bold **text** to <strong>text</strong>
        html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

        // Convert markdown-style italic *text* to <em>text</em> (but not **)
        html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');

        // Convert markdown-style headers
        html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
        html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
        html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

        // Convert lines starting with * or - to list items
        const lines = html.split('\n');
        let inList = false;
        const result: string[] = [];

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const listMatch = line.match(/^[\s]*[*\-•]\s+(.+)$/);

            if (listMatch) {
                if (!inList) {
                    result.push('<ul>');
                    inList = true;
                }
                result.push(`<li>${listMatch[1]}</li>`);
            } else {
                if (inList) {
                    result.push('</ul>');
                    inList = false;
                }
                // Convert double newlines to paragraph breaks
                if (line.trim() === '') {
                    result.push('</p><p>');
                } else {
                    result.push(line);
                }
            }
        }

        if (inList) {
            result.push('</ul>');
        }

        html = result.join('\n');

        // Wrap in paragraph if not already wrapped
        if (!html.startsWith('<')) {
            html = '<p>' + html + '</p>';
        }

        // Clean up empty paragraphs
        html = html.replace(/<p>\s*<\/p>/g, '');
        html = html.replace(/<p><\/p><p>/g, '<p>');

        return html;
    }
}
